import React, { useEffect, useRef } from 'react';
import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { useQueryClient } from '@cd/react-query';
import { column } from './commonCSS';
import { useMutationFetch } from './useQueryFetch';
import { useStore } from './store';
import TextField2FA from './TextField2FA';

/**
 * Email 2FA: mails a code to the account email on mount, then checks the
 * code the user types against `finishUrl` (which answers { isValid }).
 * purpose: 'setup' (turn email 2FA on) | 'disable' (turn it off)
 */
const EmailCodeStep = ({ purpose, finishUrl, successMessage, intro }) => {
	const [value, setValue] = React.useState('');
	const [showIsInvalid, setShowIsInvalid] = React.useState(false);
	const queryClient = useQueryClient();
	const { dispatch } = useStore();
	const send = useMutationFetch('/account/2fa/email/send');
	const finish = useMutationFetch(finishUrl, {
		onSuccess: () => {
			queryClient.refetchQueries('fetch2fasettings');
		}
	});

	// One mail per opening, even when the effect runs twice (StrictMode).
	const sentOnce = useRef(false);
	useEffect(() => {
		if (sentOnce.current) return;
		sentOnce.current = true;
		send.mutate({ purpose });
	}, []);

	useEffect(() => {
		if (finish.data?.isValid === false) {
			setShowIsInvalid(true);
		} else if (finish.data?.isValid === true) {
			dispatch({ type: 'close dialog' });
			dispatch({ type: 'post success', message: successMessage });
		}
	}, [finish.data]);

	return (
		<Box css={column} sx={{ alignItems: 'center', gap: 1 }}>
			{intro && (
				<Typography variant="body2" sx={{ pb: 1 }}>
					{intro}
				</Typography>
			)}
			{send.isLoading ? (
				<CircularProgress size={24} />
			) : (
				<Typography variant="body2">
					{send.data?.to
						? `Enter the 6 digit code we emailed to ${send.data.to}.`
						: 'The code could not be sent. Try again.'}
				</Typography>
			)}
			<TextField2FA
				sx={{ mt: 2, position: 'relative' }}
				value={value}
				setValue={setValue}
				showIsInvalid={showIsInvalid}
				setShowIsInvalid={setShowIsInvalid}
				onClick={e => {
					finish.mutate({ token: value });
				}}
			/>
			<Link
				component="button"
				type="button"
				variant="body2"
				sx={{ mt: 1 }}
				disabled={send.isLoading}
				onClick={() => {
					setValue('');
					setShowIsInvalid(false);
					send.mutate({ purpose });
				}}>
				{send.data?.to ? 'Resend code' : 'Send code'}
			</Link>
		</Box>
	);
};

export default EmailCodeStep;
